import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AccessibilityInfo,
  Animated,
  Easing,
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions
} from 'react-native'


import { LinearGradient } from 'expo-linear-gradient'
import Svg, { Defs, RadialGradient, Stop, Ellipse, Rect, Filter, FeGaussianBlur } from 'react-native-svg'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { Suit, Value } from 'pokersolver'
import { DS } from '@/design-system'

import { WebWrapper } from '@/components/WebWrapper'
import Card from "@/components/Mini/Card"
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'

//import { Footer } from '../../components/Mini/Footer'

import { palette } from '../../theme/palette'

import * as cth from '@/router/(root)/cth/cthModule'
import { CthBackground as Background } from "@/components/Mini/Background"
import { CthHeader as Header } from "@/components/Mini/Header"
import { CthStatusBar as StatusBar } from "@/components/Mini/StatusBar"
import { PrimaryBtn, SecondaryBtn } from "@/components/Mini/Button"

import { defaultTheme } from "@/components/Mini/theme"

import { FAN_SIZE, FAN_ANGLE_STEP_DEG, getFanSlot } from './fanGeometry'

/*import {
  useCthUser,
  useCthStore,
  useCthQueryStatus,
  useHandleClose,
  useHandleResults
} from '../../../router/(root)/cth/cthModule'*/

type CthGameHandlers = {
  handleSubmit: () => Promise<void>
  handleNextRound: () => void
}

type FeedbackState = {
  correct: boolean
  playerHandRank: string
  bestHandRank: string
  correctCombo: string[]
  explanation: string | null
}

type RoundData = {
  startingCards: string[]
  bottomCards: string[]
  roundIndex: number
  totalRounds: number
}

type MeasuredRect = {
  x: number
  y: number
  width: number
  height: number
}

type CardAnimationState = {
  x: Animated.Value // target card-center X (absolute, relative to root)
  y: Animated.Value // target card-center Y (absolute, relative to root)
  scale: Animated.Value
  rotate: Animated.Value // degrees
}

const grayscaleCardColors: Record<Suit, string> = {
  c: DS.palette['Brand/Dark Grey'],
  d: DS.palette['Brand/Dark Grey'],
  h: DS.palette['Brand/Dark Grey'],
  s: DS.palette['Brand/Dark Grey']
}

const { ROUND_COUNT: TOTAL_ROUNDS, GAME_NAME } = GAME_CONSTANTS.CTH_CONSTANTS
const BOTTOM_CARD_SIZE = 84 // pool cards (the source of the fly animation)
const FAN_CARD_SIZE = 58 // size cards appear at within the held-hand fan
const CARD_ASPECT_RATIO = 1.47
const FLY_DURATION_MS = 220
const SLOT_SCALE = FAN_CARD_SIZE / BOTTOM_CARD_SIZE
const OVERLAY_MEASURE_RETRIES = 12
const FALLBACK_CARD_SIZE = 48 // plain felt rendering while the overlay is unmeasured
const Z_INDEX_ACTIONS = 100

// Fan tuning: pivot radius controls how curved the arc is and, with the angle
// step, how far apart the cards sit. ~3.7x the (smaller) card width spreads the
// fan across nearly the full felt with only a light overlap between cards.
const FAN_RADIUS = Math.round(FAN_CARD_SIZE * 3.4)
const FAN_CENTER_LIFT = 6 // raise the fan slightly above the felt's vertical center
const FAN_START_SLOTS = 2 // first two fan positions are the starting cards
const HAND_AREA_HEIGHT = 144 // felt hand area the fan anchor fills
const CARD_RADIUS = 4 // matches PokerCard.Front's corner radius

function getCardHeight(size: number) {
  return Math.round(size * CARD_ASPECT_RATIO)
}

async function measureRelativeToRoot(root: View | null, target: View | null): Promise<MeasuredRect | null> {
  if (!root || !target) return null

  return new Promise((resolve) => {
    target.measureLayout(
      root as never,
      (x, y, width, height) => resolve({ x, y, width, height }),
      () => resolve(null)
    )
  })
}

export function CthGame() {
  const { status } = cth.useCthQueryStatus()
  const { isAdmin, isSuperAdmin, userId, userName } = cth.useCthUser()
  const handleResults = cth.useHandleResults()
  const handleClose = cth.useHandleClose()
  const playedDate = cth.useStore((state) => state.playedDate)
  const theme = cth.useStore(s => s.theme)

  const showAdminControls = isAdmin || isSuperAdmin

  const { width: windowWidth, height: windowHeight, fontScale } = useWindowDimensions()

  const [roundIndex, setRoundIndex] = useState(0)
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const [feedback, setFeedback] = useState<FeedbackState | null>(null)
  const [roundsCorrect, setRoundsCorrect] = useState(0)
  const [roundResults, setRoundResults] = useState<boolean[]>([])
  const [gameStarted, setGameStarted] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [isUnscored, setIsUnscored] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [startupError, setStartupError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [lastLoadedRound, setLastLoadedRound] = useState<RoundData | null>(null)
  const [roundQueryEnabled, setRoundQueryEnabled] = useState(false)
  const [overlayReady, setOverlayReady] = useState(false)
  const [fanAnchorRect, setFanAnchorRect] = useState<MeasuredRect | null>(null)
  const [feltSize, setFeltSize] = useState<{ width: number; height: number } | null>(null)
  const [footerHeight, setFooterHeight] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [reduceMotionEnabled, setReduceMotionEnabled] = useState(false)
  const [contentLayoutVersion, setContentLayoutVersion] = useState(0)

  const roundStartTime = useRef(Date.now())
  const rootRef = useRef<View>(null)
  const scrollRef = useRef<ScrollView>(null)
  const bottomCardRefs = useRef<Record<string, View | null>>({})
  const fanAnchorRef = useRef<View>(null)
  const cardAnimations = useRef<Record<string, CardAnimationState>>({})
  const scrollY = useRef(new Animated.Value(0)).current
  const submitOpacity = useRef(new Animated.Value(0)).current
  const pendingSelection = useRef<string[]>([])
  const currentAnimation = useRef<Animated.CompositeAnimation | null>(null)
  const animationGeneration = useRef(0)
  const flyDuration = useRef(FLY_DURATION_MS)
  const secretTapCount = useRef(0)
  const secretTapTime = useRef(0)
  const initializedRef = useRef(false)

  const startCthGame = useMutation(api.cth.mutations.startCthGame)
  const submitCthSelection = useMutation(api.cth.mutations.submitCthSelection)

  const roundData = useQuery(
    api.cth.queries.getCthRound,
    gameStarted && userId && roundQueryEnabled ? { userId, playedDate, roundIndex } : 'skip'
  )

  const visibleRoundData = roundData ?? (feedback ? lastLoadedRound : null)
  const bottomCards = visibleRoundData?.bottomCards ?? []

  const showSubmit = !feedback && selectedCards.length === 3 && !isAnimating

  const getCardAnimation = useCallback((card: string) => {
    const existing = cardAnimations.current[card]
    if (existing) return existing

    const next = {
      x: new Animated.Value(0),
      y: new Animated.Value(0),
      scale: new Animated.Value(1),
      rotate: new Animated.Value(0)
    }
    cardAnimations.current[card] = next
    return next
  }, [])

  const stopCurrentAnimation = useCallback(() => {
    if (currentAnimation.current) {
      currentAnimation.current.stop()
      currentAnimation.current = null
    }
  }, [])

  const measureLayouts = useCallback(async () => {
    if (!rootRef.current || bottomCards.length === 0) return null

    const bottomEntries = await Promise.all(
      bottomCards.map(async (card) => {
        const rect = await measureRelativeToRoot(rootRef.current, bottomCardRefs.current[card])
        return [card, rect] as const
      })
    )
    const anchorRect = await measureRelativeToRoot(rootRef.current, fanAnchorRef.current)

    if (bottomEntries.some(([, rect]) => !rect) || !anchorRect) {
      return null
    }

    setFanAnchorRect(anchorRect)

    return {
      bottomRects: Object.fromEntries(bottomEntries) as Record<string, MeasuredRect>,
      anchorRect
    }
  }, [bottomCards])

  /** Center + rotation for a given position within the held-hand fan. */
  const fanSlotFor = useCallback((fanIndex: number, anchor: MeasuredRect) => {
    return getFanSlot(fanIndex, FAN_SIZE, {
      centerX: anchor.x + anchor.width / 2,
      centerY: anchor.y + anchor.height / 2 - FAN_CENTER_LIFT,
      radius: FAN_RADIUS,
      angleStepDeg: FAN_ANGLE_STEP_DEG
    })
  }, [])

  /** Target center/scale/rotation for a pool card, given the current selection. */
  const cardTarget = useCallback(
    (
      card: string,
      selection: string[],
      layouts: { bottomRects: Record<string, MeasuredRect>; anchorRect: MeasuredRect }
    ) => {
      const slotIndex = selection.indexOf(card)
      if (slotIndex >= 0) {
        const slot = fanSlotFor(FAN_START_SLOTS + slotIndex, layouts.anchorRect)
        return { x: slot.cx, y: slot.cy, scale: SLOT_SCALE, rotate: slot.rotateDeg }
      }
      const rect = layouts.bottomRects[card]
      return {
        x: rect.x + BOTTOM_CARD_SIZE / 2,
        y: rect.y + getCardHeight(BOTTOM_CARD_SIZE) / 2,
        scale: 1,
        rotate: 0
      }
    },
    [fanSlotFor]
  )

  const syncCardPositions = useCallback(
    async (selection: string[]) => {
      const layouts = await measureLayouts()
      if (!layouts) return false

      bottomCards.forEach((card) => {
        const animation = getCardAnimation(card)
        const target = cardTarget(card, selection, layouts)

        animation.x.setValue(target.x)
        animation.y.setValue(target.y)
        animation.scale.setValue(target.scale)
        animation.rotate.setValue(target.rotate)
      })

      return true
    },
    [bottomCards, cardTarget, getCardAnimation, measureLayouts]
  )

  const runSelectionAnimation = useCallback(
    async (nextSelectedCards: string[]) => {
      stopCurrentAnimation()
      pendingSelection.current = nextSelectedCards
      const generation = ++animationGeneration.current
      setIsAnimating(true)

      if (reduceMotionEnabled) {
        setSelectedCards(nextSelectedCards)
        await syncCardPositions(nextSelectedCards)
        setIsAnimating(false)
        return
      }

      const layouts = await measureLayouts()
      if (animationGeneration.current !== generation) return

      if (!layouts) {
        setSelectedCards(nextSelectedCards)
        await syncCardPositions(nextSelectedCards)
        setIsAnimating(false)
        return
      }

      setSelectedCards(nextSelectedCards)

      const animations = bottomCards.flatMap((card) => {
        const animation = getCardAnimation(card)
        const target = cardTarget(card, nextSelectedCards, layouts)
        const ease = Easing.bezier(0.2, 0.8, 0.2, 1)

        return [
          Animated.timing(animation.x, {
            toValue: target.x,
            duration: flyDuration.current,
            easing: ease,
            useNativeDriver: true
          }),
          Animated.timing(animation.y, {
            toValue: target.y,
            duration: flyDuration.current,
            easing: ease,
            useNativeDriver: true
          }),
          Animated.timing(animation.scale, {
            toValue: target.scale,
            duration: flyDuration.current,
            easing: ease,
            useNativeDriver: true
          }),
          Animated.timing(animation.rotate, {
            toValue: target.rotate,
            duration: flyDuration.current,
            easing: ease,
            useNativeDriver: true
          })
        ]
      })

      await new Promise<void>((resolve) => {
        currentAnimation.current = Animated.parallel(animations)
        currentAnimation.current.start(() => {
          currentAnimation.current = null
          resolve()
        })
      })

      if (animationGeneration.current === generation) {
        setIsAnimating(false)
      }
    },
    [
      bottomCards,
      cardTarget,
      getCardAnimation,
      measureLayouts,
      reduceMotionEnabled,
      stopCurrentAnimation,
      syncCardPositions
    ]
  )

  const toggleCard = useCallback(
    (card: string) => {
      if (feedback) return
      const current = pendingSelection.current
      if (current.includes(card) || current.length >= 3) return
      void runSelectionAnimation([...current, card])
    },
    [feedback, runSelectionAnimation]
  )

  const removeFromSlot = useCallback(
    (card: string) => {
      if (feedback) return
      const current = pendingSelection.current
      if (!current.includes(card)) return
      void runSelectionAnimation(current.filter((c) => c !== card))
    },
    [feedback, runSelectionAnimation]
  )

  const handleContentLayout = useCallback((_event: LayoutChangeEvent) => {
    setContentLayoutVersion((prev) => prev + 1)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (selectedCards.length !== 3 || submitting) return
    setSubmitting(true)

    const elapsedMs = Date.now() - roundStartTime.current

    try {
      if (!userId) return

      const result = await submitCthSelection({
        userId,
        userName: userName ?? undefined,
        playedDate,
        roundIndex,
        selectedCards,
        elapsedMs
      })

      setFeedback({
        correct: result.correct,
        playerHandRank: result.playerHandRank,
        bestHandRank: result.bestHandRank,
        correctCombo: result.correctCombo,
        explanation: result.explanation
      })

      const newResults = [...roundResults, result.correct]
      setRoundResults(newResults)

      if (result.scored) {
        setRoundsCorrect(result.roundsCorrect ?? roundsCorrect)
        if (result.gameOver) {
          setGameOver(true)
        }
      } else {
        // Unscored path
        if (result.remainingRounds === 0) {
          setGameOver(true)
        }
      }
    } catch (e) {
      console.error('Failed to submit selection:', e)
      setSubmitError(e instanceof Error ? e.message : 'Unable to submit your answer right now.')
    } finally {
      setSubmitting(false)
    }
  }, [
    selectedCards,
    submitting,
    userId,
    userName,
    playedDate,
    roundIndex,
    submitCthSelection,
    roundResults,
    roundsCorrect
  ])

  const handleNextRound = useCallback(() => {
    if (gameOver) {
      handleResults()
      return
    }
    scrollRef.current?.scrollTo({ y: 0, animated: false })
    scrollY.setValue(0)
    setRoundIndex((prev) => prev + 1)
  }, [gameOver, handleResults, scrollY])

  const handlers = { handleSubmit, handleNextRound }

  //cache round date to prevent flashing
  useEffect(() => {
    if (roundData) {
      setLastLoadedRound(roundData)
    }
  }, [roundData])

  //set reduce motion if enabled in settings
  useEffect(() => {
    let mounted = true
    AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (mounted) {
          setReduceMotionEnabled(enabled)
        }
      })
      .catch(() => {
        if (mounted) {
          setReduceMotionEnabled(false)
        }
      })

    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', (enabled) => {
      setReduceMotionEnabled(enabled)
    })

    return () => {
      mounted = false
      subscription.remove()
    }
  }, [])

  // Start the game on mount
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!userId) {
          return
        }

        setStartupError(null)
        setSubmitError(null)
        setRoundQueryEnabled(false)
        const result = await startCthGame({ userId, playedDate, todayDate: playedDate })
        if (cancelled) return

        if (result.status === 'already_completed') {
          setIsUnscored(true)
          setRoundIndex(0)
          setRoundResults([])
          setGameOver(false)
          setRoundQueryEnabled(true)
        } else if (result.status === 'resumed') {
          // Query will load the current round
        } else {
          initializedRef.current = true
          setIsUnscored(false)
          setRoundIndex(0)
          setRoundsCorrect(0)
          setRoundResults([])
          setGameOver(false)
          setRoundQueryEnabled(true)
        }
        setGameStarted(true)
      } catch (e) {
        console.error('Failed to start CTH game:', e)
        if (!cancelled) {
          setStartupError(e instanceof Error ? e.message : `Unable to load ${GAME_NAME} right now.`)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [playedDate, startCthGame, userId])

  // Load in-progress state (once on mount only, not reactively)
  useEffect(() => {
    if (initializedRef.current) return
    if (status?.status === 'in_progress' && !isUnscored) {
      initializedRef.current = true
      setRoundIndex(status.roundIndex)
      setRoundsCorrect(status.roundsCorrect)
      setRoundResults(status.roundResults.map((result) => result.correct))
      setRoundQueryEnabled(true)
    }
  }, [status, isUnscored])

  // Reset round state when roundIndex changes
  useEffect(() => {
    stopCurrentAnimation()
    pendingSelection.current = []
    animationGeneration.current++
    setSelectedCards([])
    setIsAnimating(false)
    setFeedback(null)
    setSubmitError(null)
    setOverlayReady(false)
    roundStartTime.current = Date.now()
  }, [roundIndex])

  //sync animated card positions with static card layout
  useEffect(() => {
    if (!visibleRoundData || currentAnimation.current) return

    let cancelled = false
    let attempts = 0

    const trySync = () => {
      requestAnimationFrame(() => {
        syncCardPositions(selectedCards).then((ready) => {
          if (cancelled) return

          if (ready) {
            setOverlayReady(true)
            return
          }

          attempts += 1
          if (attempts < OVERLAY_MEASURE_RETRIES) {
            trySync()
          }
        })
      })
    }

    trySync()

    return () => {
      cancelled = true
    }
  }, [
    contentLayoutVersion,
    fontScale,
    roundIndex,
    selectedCards,
    syncCardPositions,
    visibleRoundData,
    windowHeight,
    windowWidth
  ])

  //animate submit button
  useEffect(() => {
    if (showSubmit) {
      Animated.timing(submitOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true
      }).start()
    } else {
      submitOpacity.setValue(0)
    }
  }, [showSubmit, submitOpacity])

  // Streak indicator circles
  const streakIndicator = useMemo(() => {
    return Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
      if (i < roundResults.length) {
        return roundResults[i] ? 'correct' : 'incorrect'
      }
      if (i === roundIndex && !feedback) {
        return 'current'
      }
      return 'empty'
    })
  }, [roundResults, roundIndex, feedback])

  if (startupError) {
    return (
      <Background>
            {/*match pp */}
            {/*<Header 
              accentColor={T.accent} 
              iconColor="Global/Black"
              leftPress={handleClose} 
            />*/}
            <View style={styles.center}>
              <DS.Frame gap="12" alignItems="center" style={styles.errorCard}>
                <DS.Text variant="Body Medium/Medium" style={{color: theme.textPrimary}}>
                  {startupError}
                </DS.Text>
                <BackBtn handler={handleClose}/>
                {/*<DS.Button variant="Large/Secondary" type="On Light" textColor="Brand/Maroon" onPress={handleClose}>
                  Back
                </DS.Button>*/}
                
              </DS.Frame>
            </View>

      </Background>
    )
  }

  if (!visibleRoundData) {
    return (
      <Background>

            <View style={styles.center}>
              <DS.Text variant="Body Medium/Medium" style={{color: theme.accent}}>
                Loading round...
              </DS.Text>
            </View>
      </Background>
    )
  }

  const { startingCards } = visibleRoundData
  const fanCenter = fanAnchorRect
    ? {
        centerX: fanAnchorRect.x + fanAnchorRect.width / 2,
        centerY: fanAnchorRect.y + fanAnchorRect.height / 2 - FAN_CENTER_LIFT
      }
    : null
  const emptyFanSlots = fanCenter
    ? Array.from(
        { length: FAN_SIZE - FAN_START_SLOTS - selectedCards.length },
        (_, i) => FAN_START_SLOTS + selectedCards.length + i
      )
    : []

  return (
    <Background>

          <Header
            title={`Hand ${Math.min(roundIndex + 1, TOTAL_ROUNDS)} / ${TOTAL_ROUNDS}`}
            handler={handleClose}
          />

          <View style={styles.contentArea} ref={rootRef} onLayout={handleContentLayout}>
            <Animated.ScrollView
              ref={scrollRef}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
              scrollEventThrottle={16}
              onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: true })}
            >
              {/* Streak indicator */}
              {/*<DS.Frame row justifyContent="center" gap="6" style={styles.streakRow}>
                {streakIndicator.map((state, i) => (
                  <View
                    key={i}
                    style={[
                      styles.streakDot,
                      state === 'correct' && styles.streakCorrect,
                      state === 'incorrect' && styles.streakIncorrect,
                      state === 'current' && styles.streakCurrent,
                      state === 'empty' && styles.streakEmpty
                    ]}
                  />
                ))}
              </DS.Frame>*/}

              {/* Felt — the held-hand fan is drawn in the overlay, anchored here */}
              <View style={styles.tableShell}>
                <View style={[styles.tableRail, {backgroundColor: theme.feltRail}]}>
                  <View
                    style={[styles.tableFelt, {backgroundColor: theme.feltBed}]}
                    onLayout={(e) =>
                      setFeltSize({ width: e.nativeEvent.layout.width, height: e.nativeEvent.layout.height })
                    }
                  >
                    <View pointerEvents="none" style={styles.feltClip}>
                      {/*<FeltSpotlight theme={theme}/>*/}
                      {feltSize && (
                        <>
                          {/* Underlighting: a blurred stroke of the felt's own pill shape,
                          clipped so the inner half glows uniformly around the edge. */}
                          <FeltGlow
                            width={feltSize.width}
                            height={feltSize.height}
                            filterId="cthGlowBase"
                            color={theme.ringGray}
                            peak={0.32}
                            blur={11}
                            strokeWidth={6}
                          />
                          {feedback && (
                            <FeltGlow
                              width={feltSize.width}
                              height={feltSize.height}
                              filterId="cthGlowFeedback"
                              color={feedback.correct ? theme.glowWin : theme.glowLose}
                              peak={1}
                              blur={feedback.correct ? 16 : 18}
                              strokeWidth={feedback.correct ? 12 : 17}
                              fade
                            />
                          )}
                        </>
                      )}
                    </View>
                    <View ref={fanAnchorRef} collapsable={false} style={styles.fanAnchor} />
                    {/* Fallback: keep the hand visible (and selections removable) if the
                    overlay never measures — the fan itself only renders when it has. */}
                    {!overlayReady && (
                      <View pointerEvents="box-none" style={styles.feltFallbackRow}>
                        {startingCards.map((card: string) => (
                          <PokerCard.Front
                            key={card}
                            value={card[0] as Value}
                            suit={card[1] as Suit}
                            size={FALLBACK_CARD_SIZE}
                            shadow={false}
                          />
                        ))}
                        {selectedCards.map((card) => (
                          <Pressable key={card} onPress={() => removeFromSlot(card)} disabled={!!feedback}>
                            <PokerCard.Front
                              value={card[0] as Value}
                              suit={card[1] as Suit}
                              size={FALLBACK_CARD_SIZE}
                              shadow={false}
                            />
                          </Pressable>
                        ))}
                      </View>
                    )}
                  </View>
                </View>
              </View>

              {/* Label */}
              <DS.Text
                variant="Body Small/Regular"
                color="Brand/Light Grey"
                textAlign="center"
                style={[styles.pickLabel, {color: theme.textDefault}]}
              >
                Pick 3 cards
              </DS.Text>

              {/* Bottom cards */}
              <View style={styles.bottomCardsGrid}>
                <View style={styles.bottomCardsRow}>
                  {bottomCards.slice(0, 3).map((card: string) => {
                    const isSelected = selectedCards.includes(card)
                    return (
                      <View
                        key={card}
                        style={styles.bottomCardShell}
                        ref={(node) => {
                          bottomCardRefs.current[card] = node
                        }}
                      >
                        <View style={styles.bottomGhostCard}>
                          <PokerCard.Front
                            value={card[0] as Value}
                            suit={card[1] as Suit}
                            size={BOTTOM_CARD_SIZE}
                            shadow={false}
                          />
                        </View>
                        {!overlayReady && !isSelected && (
                          <Pressable
                            onPress={() => toggleCard(card)}
                            disabled={!!feedback}
                            style={styles.bottomFallbackCard}
                          >
                            <DepthCard size={BOTTOM_CARD_SIZE}>
                              <PokerCard.Front
                                value={card[0] as Value}
                                suit={card[1] as Suit}
                                size={BOTTOM_CARD_SIZE}
                                shadow={CARD_SHADOW}
                              />
                            </DepthCard>
                          </Pressable>
                        )}
                      </View>
                    )
                  })}
                </View>
                <View style={styles.bottomCardsRow}>
                  {showAdminControls && (
                    <Pressable
                      style={styles.secretTapZone}
                      onPress={() => {
                        if (isAnimating) return
                        const now = Date.now()
                        if (now - secretTapTime.current > 1500) secretTapCount.current = 0
                        secretTapTime.current = now
                        secretTapCount.current += 1
                        if (secretTapCount.current >= 3) {
                          secretTapCount.current = 0
                          flyDuration.current = flyDuration.current === FLY_DURATION_MS ? 6200 : FLY_DURATION_MS
                        }
                      }}
                    />
                  )}
                  {bottomCards.slice(3).map((card: string) => {
                    const isSelected = selectedCards.includes(card)
                    return (
                      <View
                        key={card}
                        style={styles.bottomCardShell}
                        ref={(node) => {
                          bottomCardRefs.current[card] = node
                        }}
                      >
                        <View style={styles.bottomGhostCard}>
                          <PokerCard.Front
                            value={card[0] as Value}
                            suit={card[1] as Suit}
                            size={BOTTOM_CARD_SIZE}
                            shadow={false}
                          />
                        </View>
                        {!overlayReady && !isSelected && (
                          <Pressable
                            onPress={() => toggleCard(card)}
                            disabled={!!feedback}
                            style={styles.bottomFallbackCard}
                          >
                            <DepthCard size={BOTTOM_CARD_SIZE}>
                              <PokerCard.Front
                                value={card[0] as Value}
                                suit={card[1] as Suit}
                                size={BOTTOM_CARD_SIZE}
                                shadow={CARD_SHADOW}
                              />
                            </DepthCard>
                          </Pressable>
                        )}
                      </View>
                    )
                  })}
                </View>
              </View>

              {submitError && !feedback && (
                <View style={styles.feedbackBanner}>
                  <DS.Text variant="Body Medium/Medium" textAlign="center" style={{ color: theme.danger }}>
                    {submitError}
                  </DS.Text>
                </View>
              )}
            </Animated.ScrollView>

            {/* Actions pinned below scroll */}
            <View style={styles.footerWrap} onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}>
              <DS.Frame gap="12" style={[styles.footer]}>
                <FooterContent
                  showSubmit={showSubmit}
                  submitOpacity={submitOpacity}
                  submitting={submitting}
                  feedback={feedback}
                  gameOver={gameOver}
                  handlers={handlers}
                />
              </DS.Frame>
      
            </View>

            {overlayReady && (
              <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
                {/* Starting cards — static left side of the fan */}
                {fanCenter &&
                  startingCards.map((card: string, i: number) => {
                    const slot = getFanSlot(i, FAN_SIZE, {
                      ...fanCenter,
                      radius: FAN_RADIUS,
                      angleStepDeg: FAN_ANGLE_STEP_DEG
                    })
                    const w = FAN_CARD_SIZE
                    const h = getCardHeight(FAN_CARD_SIZE)
                    return (
                      <Animated.View
                        key={`start-${card}`}
                        pointerEvents="none"
                        style={[
                          styles.overlayCard,
                          {
                            width: w,
                            height: h,
                            zIndex: i,
                            transform: [
                              { translateX: slot.cx - w / 2 },
                              { translateY: Animated.subtract(slot.cy - h / 2, scrollY) },
                              { rotate: `${slot.rotateDeg}deg` }
                            ]
                          }
                        ]}
                      >
                        <DepthCard size={w}>
                          <PokerCard.Front
                            value={card[0] as Value}
                            suit={card[1] as Suit}
                            size={w}
                            shadow={CARD_SHADOW}
                          />
                        </DepthCard>
                      </Animated.View>
                    )
                  })}

                {/* Empty fan positions — dashed placeholders that fill as you pick */}
                {fanCenter &&
                  emptyFanSlots.map((fanIndex) => {
                    const slot = getFanSlot(fanIndex, FAN_SIZE, {
                      ...fanCenter,
                      radius: FAN_RADIUS,
                      angleStepDeg: FAN_ANGLE_STEP_DEG
                    })
                    const w = FAN_CARD_SIZE
                    const h = getCardHeight(FAN_CARD_SIZE)
                    return (
                      <Animated.View
                        key={`empty-${fanIndex}`}
                        pointerEvents="none"
                        style={[
                          styles.overlayCard,
                          {
                            width: w,
                            height: h,
                            zIndex: fanIndex,
                            transform: [
                              { translateX: slot.cx - w / 2 },
                              { translateY: Animated.subtract(slot.cy - h / 2, scrollY) },
                              { rotate: `${slot.rotateDeg}deg` }
                            ]
                          }
                        ]}
                      >
                        <View style={styles.fanEmptySlot} />
                      </Animated.View>
                    )
                  })}

                {/* Pool cards — fly between the grid and their fan position */}
                {bottomCards.map((card) => {
                  const animation = getCardAnimation(card)
                  const isSelected = selectedCards.includes(card)
                  const isWrongSelected = !!(
                    feedback &&
                    !feedback.correct &&
                    isSelected &&
                    !feedback.correctCombo.includes(card)
                  )
                  const scoredFade = !!(feedback && !isSelected && !feedback.correctCombo.includes(card))
                  const w = BOTTOM_CARD_SIZE
                  const h = getCardHeight(BOTTOM_CARD_SIZE)
                  const rotate = animation.rotate.interpolate({
                    inputRange: [-180, 180],
                    outputRange: ['-180deg', '180deg']
                  })
                  const translateX = Animated.subtract(animation.x, w / 2)
                  const translateY = Animated.subtract(Animated.subtract(animation.y, scrollY), h / 2)
                  // Selected cards ride above the pool (so they fly over, not behind)
                  // and stack left-over-right within the fan via their selection order.
                  const zIndex = isSelected ? 20 + selectedCards.indexOf(card) : 10

                  return (
                    <Animated.View
                      key={`overlay-${card}`}
                      style={[
                        styles.overlayCard,
                        {
                          width: w,
                          height: h,
                          zIndex,
                          transform: [{ translateX }, { translateY }, { rotate }, { scale: animation.scale }]
                        }
                      ]}
                    >
                      <Pressable
                        onPress={() => {
                          if (isSelected) {
                            void removeFromSlot(card)
                          } else {
                            void toggleCard(card)
                          }
                        }}
                        disabled={!!feedback || (!isSelected && selectedCards.length >= 3)}
                        style={scoredFade && styles.bottomCardFaded}
                      >
                        {isSelected ? (
                          isWrongSelected ? (
                            <DepthCard size={w} style={styles.incorrectSelectedCard}>
                              <PokerCard.Front
                                value={card[0] as Value}
                                suit={card[1] as Suit}
                                size={w}
                                colorSet={grayscaleCardColors}
                                valueColor={DS.palette['Brand/Dark Grey']}
                                shadow={CARD_SHADOW}
                              />
                            </DepthCard>
                          ) : (
                            <DepthCard size={w}>
                              <FlippableCard card={card} size={w} showBack={false} shadow={CARD_SHADOW} />
                            </DepthCard>
                          )
                        ) : (
                          <View style={styles.cardFace}>
                            <DepthCard size={w}>
                              <PokerCard.Front
                                value={card[0] as Value}
                                suit={card[1] as Suit}
                                size={w}
                                shadow={CARD_SHADOW}
                              />
                            </DepthCard>
                            {feedback && !feedback.correct && feedback.correctCombo.includes(card) && 
                              <PulsingGlow 
                                theme={theme}
                              />
                            }
                          </View>
                        )}
                      </Pressable>
                    </Animated.View>
                  )
                })}
              </View>
            )}

            {/* Result docked just above the Next Round button */}
            {feedback && (
              <View pointerEvents="box-none" style={[styles.resultDock, { bottom: footerHeight + 4 }]}>
                <ResultPanel feedback={feedback} theme={theme} />
              </View>
            )}
          </View>

    </Background>
  )
}

/** Soft radial spotlight on the felt, lighting the held hand from above. */
function FeltSpotlight({ theme }: { theme: typeof defaultTheme }) {
  return (
    <Svg pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Defs>
        <RadialGradient id="cthSpotlight" cx="50%" cy="45%" r="65%">
          <Stop offset="0" stopColor={theme.feltSpotlight} stopOpacity={0.16} />
          <Stop offset="1" stopColor={theme.feltSpotlight} stopOpacity={0} />
        </RadialGradient>
      </Defs>
      <Ellipse cx="50%" cy="45%" rx="66%" ry="62%" fill="url(#cthSpotlight)" />
    </Svg>
  )
}

/**
 * Inner-edge underlighting: the felt's own pill shape drawn as a stroked stadium
 * and Gaussian-blurred, then clipped to the felt (by feltClip) so only the inner
 * half of the blur shows — a soft glow that hugs the entire inner edge uniformly.
 * A faint gray layer sits at rest; a gold (win) / red (miss) layer fades in on
 * feedback. `filterId` must be unique per layer (svg filter ids are global).
 */
function FeltGlow({
  width,
  height,
  color,
  peak,
  blur,
  strokeWidth,
  filterId,
  fade
}: {
  width: number
  height: number
  color: string
  peak: number
  blur: number
  strokeWidth: number
  filterId: string
  fade?: boolean
}) {
  const opacity = useRef(new Animated.Value(fade ? 0 : 1)).current

  useEffect(() => {
    if (!fade) return
    Animated.timing(opacity, {
      toValue: 1,
      duration: 450,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true
    }).start()
  }, [fade, opacity])

  return (
    <Animated.View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { opacity }, Platform.OS !== 'ios' && { filter: `blur(${blur}px)` }]}
    >
      <Svg width={width} height={height}>
        {Platform.OS === 'ios' && (
          <Defs>
            <Filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
              <FeGaussianBlur stdDeviation={blur} />
            </Filter>
          </Defs>
        )}
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          rx={height / 2}
          ry={height / 2}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeOpacity={peak}
          filter={Platform.OS === 'ios' ? `url(#${filterId})` : undefined}
        />
      </Svg>
    </Animated.View>
  )
}

/**
 * Card shadow used across the fan and pool. Applied to the card's own (white,
 * opaque) container via PokerCard's `shadow` prop so it casts correctly on both
 * iOS (shadow*) and Android (elevation) — a transparent wrapper would not.
 */
const CARD_SHADOW = {
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.35,
  shadowRadius: 7,
  elevation: 7
} as const

/**
 * Adds a top-down gloss sheen and a hairline top highlight over a card so the
 * flat art reads as a glossy, lifted surface. The card supplies its own shadow
 * (see CARD_SHADOW); the sheen is aligned, not clipped, to avoid clipping it.
 */
function DepthCard({
  size,
  children,
  style
}: { size: number; children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const height = getCardHeight(size)
  return (
    <View style={[{ width: size, height }, style]}>
      {children}
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(255,255,255,0.25)', 'rgba(255,255,255,0.04)', 'rgba(255,255,255,0)']}
        locations={[0, 0.45, 1]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={[styles.cardSheen, { borderRadius: CARD_RADIUS }]}
      />
      <View pointerEvents="none" style={styles.cardTopHighlight} />
    </View>
  )
}

/** Result + explanation panel that slides down under the table on submit. */
function ResultPanel({ feedback, theme }: { feedback: FeedbackState; theme: typeof defaultTheme }) {
  const anim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    Animated.timing(anim, { toValue: 1, duration: 280, easing: Easing.out(Easing.ease), useNativeDriver: true }).start()
  }, [anim])

  const translateY = anim.interpolate({ inputRange: [0, 1], outputRange: [28, 0] })

  return (
    <Animated.View style={{ width: '100%', opacity: anim, transform: [{ translateY }] }}>
      <Card>
      {/*<LinearGradient colors={['rgba(11,46,34,0.94)', 'rgba(5,20,14,0.94)']} style={[styles.resultPanel, {borderColor: theme.surfaceBorder,}]}>*/}
        <DS.Text
          variant="Heading 4/Medium"
          textAlign="center"
          style={{ color: feedback.correct ? theme.success : theme.danger }}
        >
          {feedback.correct ? 'Correct' : 'Incorrect'}
        </DS.Text>
        <DS.Text variant="Body Medium/Regular" textAlign="center" style={{color: theme.textPrimary}}>
          Your hand: {feedback.playerHandRank}
        </DS.Text>
        {!feedback.correct && (
          <DS.Text variant="Body Medium/Regular" textAlign="center" style={{color: theme.textPrimary}}>
            Best hand: {feedback.bestHandRank}
          </DS.Text>
        )}
        {feedback.explanation && (
          <DS.Text variant="Body Small/Regular" textAlign="center" style={{color: theme.textPrimary}}>
            {feedback.explanation}
          </DS.Text>
        )}
        </Card>
      {/*</LinearGradient>*/}
    </Animated.View>
  )
}

/** Pulsing gold border — used for the correct card(s) and the table felt. */
function PulsingGlow({ style, theme }: { style?: StyleProp<ViewStyle>; theme: typeof defaultTheme }) {

  const pulse = useRef(new Animated.Value(0)).current

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: true })
      ])
    )
    loop.start()
    return () => loop.stop()
  }, [pulse])

  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.45, 1] })
  const borderColor = theme.highlightGold
  const shadowColor = theme.highlightGold
  return <Animated.View pointerEvents="none" style={[style ?? styles.correctGlow, { opacity, borderColor, shadowColor }]} />
}

function FlippableCard({
  card,
  size,
  showBack,
  shadow
}: { card: string; size: number; showBack: boolean; shadow: React.ComponentProps<typeof PokerCard.Front>['shadow'] }) {
  const flipAnim = useRef(new Animated.Value(0)).current
  const isBack = useRef(showBack)

  useEffect(() => {
    if (showBack === isBack.current) return
    isBack.current = showBack
    Animated.timing(flipAnim, {
      toValue: showBack ? 1 : 0,
      duration: 400,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true
    }).start()
  }, [showBack, flipAnim])

  const frontRotate = flipAnim.interpolate({ inputRange: [0, 0.5, 1], outputRange: ['0deg', '90deg', '90deg'] })
  const backRotate = flipAnim.interpolate({ inputRange: [0, 0.5, 1], outputRange: ['90deg', '90deg', '0deg'] })
  const frontOpacity = flipAnim.interpolate({ inputRange: [0, 0.49, 0.51, 1], outputRange: [1, 1, 0, 0] })
  const backOpacity = flipAnim.interpolate({ inputRange: [0, 0.49, 0.51, 1], outputRange: [0, 0, 1, 1] })

  return (
    <View style={{ width: size, height: Math.round(size * 1.47) }}>
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          {
            transform: [{ perspective: 800 }, { rotateY: frontRotate }],
            opacity: frontOpacity,
            backfaceVisibility: 'hidden'
          }
        ]}
      >
        <PokerCard.Front value={card[0] as Value} suit={card[1] as Suit} size={size} shadow={shadow} />
      </Animated.View>
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          {
            transform: [{ perspective: 800 }, { rotateY: backRotate }],
            opacity: backOpacity,
            backfaceVisibility: 'hidden'
          }
        ]}
      >
        <PokerCard.Back size={size} />
      </Animated.View>
    </View>
  )
}

function FooterContent({
  showSubmit,
  submitOpacity,
  submitting,
  feedback,
  gameOver,
  handlers
}: {
  showSubmit: boolean
  submitOpacity: Animated.Value
  submitting: boolean
  feedback: FeedbackState | null
  gameOver: boolean
  handlers: CthGameHandlers
}) {
  const showFeedbackBtn = feedback
  const showSubmitBtn = showSubmit
  const { handleSubmit, handleNextRound } = handlers

  return (
    <>
      {showFeedbackBtn ? (
        //<FooterFeedbackBtn gameOver={gameOver} handlers={handlers} />
        <FeedbackBtn gameOver={gameOver} handler={handleNextRound} opacity={submitOpacity} />
      ) : (
        //showSubmitBtn && <FooterSubmitBtn submitOpacity={submitOpacity} submitting={submitting} handlers={handlers} />
        showSubmitBtn && <SubmitBtn handler={handleSubmit} submitting={submitting} />
      )}
    </>
  )
}

function BackBtn({ handler }: { handler: () => void }) {

  const theme = cth.useStore(s => s.theme)
  const label = "Back"

  return (
    <SecondaryBtn theme={theme} handler={handler} label={label} />
  )
}

function SubmitBtn({ handler, submitting, opacity }: { handler: () => void; submitting: boolean; opacity?: Animated.Value }) {

  const theme = cth.useStore(s => s.theme)
  const label = submitting ? 'Checking...' : 'Submit'
  const isDisabled = submitting

  return (
    <Animated.View style={{ opacity }}>
      <PrimaryBtn theme={theme} handler={handler} label={label} isDisabled={isDisabled} />
    </Animated.View>
  )
}

{/*function FooterSubmitBtn({
  submitOpacity,
  submitting,
  handlers
}: { submitOpacity: Animated.Value; submitting: boolean; handlers: CthGameHandlers }) {
  const { handleSubmit } = handlers
  const opacity = submitOpacity
  const disabled = submitting
  const label = submitting ? 'Checking...' : 'Submit'

  return (
    <Animated.View style={{ opacity }}>
      <DS.Button
        variant="Large/Primary"
        type="On Light"
        onPress={handleSubmit}
        disabled={disabled}
        style={{backgroundColor: T.accent}}
      >
        {label}
      </DS.Button>
    </Animated.View>
  )
}*/}

 function FeedbackBtn({ gameOver, handler, opacity }: { gameOver: boolean; handler: () => void; opacity: Animated.Value }) {

  const theme = cth.useStore(s => s.theme)
  const label = gameOver ? 'See Results' : 'Next Round'

  return (
    <PrimaryBtn theme={theme} handler={handler} label={label} />
  )
}







/*function FooterFeedbackBtn({ gameOver, handlers }: { gameOver: boolean; handlers: CthGameHandlers }) {
  const { handleNextRound } = handlers
  const label = gameOver ? 'See Results' : 'Next Round'

  return (
    <DS.Button variant="Large/Primary" type="On Light" style={{backgroundColor: T.accent}}onPress={handleNextRound}>
      {label}
    </DS.Button>
  )
}*/

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  //textDefault: {
    //color: T.textDefault
  //},
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  contentArea: {
    flex: 1,
    overflow: 'hidden'
  },
  streakRow: {
    marginBottom: 24
  },
  streakDot: {
    width: 14,
    height: 14,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: palette.white
  },
  streakCorrect: {
    backgroundColor: DS.palette['Utility/Success']
  },
  streakIncorrect: {
    backgroundColor: DS.palette['Utility/Warning']
  },
  streakCurrent: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: palette.white
  },
  streakEmpty: {
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  tableShell: {
    paddingHorizontal: 16
  },
  tableRail: {
    borderRadius: 999,
   //backgroundColor: T.feltRail,
    padding: 16,
    shadowColor: palette.black,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.12,
    shadowRadius: 18,
    elevation: 4
  },
  tableFelt: {
    borderRadius: 999,
    //backgroundColor: T.feltBed,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.08)',
    paddingVertical: 14,
    paddingHorizontal: 18
  },
  feltClip: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 999,
    overflow: 'hidden'
  },
  fanAnchor: {
    width: '100%',
    height: HAND_AREA_HEIGHT
  },
  feltFallbackRow: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24
  },
  footerWrap: {
    zIndex: Z_INDEX_ACTIONS
  },
  resultDock: {
    position: 'absolute',
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    zIndex: Z_INDEX_ACTIONS - 10
  },
  resultPanel: {
    paddingVertical: 16,
    paddingHorizontal: 22,
    borderRadius: 18,
    borderWidth: 1,
    //borderColor: T.surfaceBorder,
    overflow: 'hidden',
    gap: 4,
    alignItems: 'center'
  },
  //resultText: {
    //color: T.textPrimary
  //},
  fanEmptySlot: {
    width: FAN_CARD_SIZE,
    height: getCardHeight(FAN_CARD_SIZE),
    borderRadius: 6,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.35)',
    borderStyle: 'dashed'
  },
  cardFace: {
    position: 'relative'
  },
  cardSheen: {
    ...StyleSheet.absoluteFillObject
  },
  cardTopHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.7)'
  },
  correctGlow: {
    position: 'absolute',
    top: -5,
    left: -5,
    right: -5,
    bottom: -5,
    borderRadius: 10,
    borderWidth: 2.5,
    shadowOpacity: 0.9,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 0 },
    elevation: 8
  },
  incorrectSelectedCard: {
    opacity: 0.5
  },
  pickLabel: {
    marginBottom: 16,
    marginTop: 24
  },
  bottomCardsGrid: {
    gap: 16,
    paddingHorizontal: 16,
    marginBottom: 24
  },
  bottomCardsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16
  },
  bottomCardShell: {
    width: BOTTOM_CARD_SIZE,
    height: getCardHeight(BOTTOM_CARD_SIZE)
  },
  bottomGhostCard: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.2
  },
  bottomFallbackCard: {
    ...StyleSheet.absoluteFillObject
  },
  bottomCardFaded: {
    opacity: 0.2
  },
  feedbackBanner: {
    paddingHorizontal: 24,
    gap: 8,
    marginBottom: 24,
    alignItems: 'center'
  },
  scrollContent: {
    paddingBottom: 8
  },
  secretTapZone: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 60,
    height: getCardHeight(BOTTOM_CARD_SIZE),
    backgroundColor: 'transparent'
  },
  overlayCard: {
    position: 'absolute',
    left: 0,
    top: 0
  },
  errorCard: {
    paddingHorizontal: 24
  }
})
